// ===========================================
// useSchoolWeek — the school's current week + a label for any date range
// ===========================================
// Used by the report pages as their default period. See utils/schoolWeek.ts.

import { useCallback, useMemo } from 'react';
import { useSchoolTimezone } from '../contexts/TenantContext';
import { useTerms } from './useAdmin';
import { schoolWeekKeys, describeDateRange, pickCurrentTerm, schoolTodayKey, type DateRangeKeys } from '../utils/schoolWeek';

export function useSchoolWeek() {
  const timeZone = useSchoolTimezone();
  const { data: terms } = useTerms();

  // Recomputed only when the zone changes; a page open across midnight keeps
  // the week it showed, which is what an admin mid-task expects.
  const week = useMemo<DateRangeKeys>(() => schoolWeekKeys(timeZone), [timeZone]);

  const describe = useCallback(
    (range: Partial<DateRangeKeys> | undefined): string | null =>
      range?.startDate && range?.endDate
        ? describeDateRange({ startDate: range.startDate, endDate: range.endDate }, terms ?? [])
        : null,
    [terms]
  );

  return { week, describe };
}

/**
 * The term the school is in today (else the next, else the latest) — the default
 * for reports that are term-shaped. `term` is undefined while terms load.
 */
export function useCurrentTerm() {
  const timeZone = useSchoolTimezone();
  const { data: terms, isLoading, isError } = useTerms();
  const term = useMemo(() => (terms ? pickCurrentTerm(terms, schoolTodayKey(timeZone)) : undefined), [terms, timeZone]);
  // `terms` is returned so a caller can NAME a term the admin picked that is not
  // the current one, without a second useTerms subscription.
  return { term, terms, isLoading, isError };
}
